import { useAuth } from '@workos-inc/authkit-react';
import { type ReactNode, useEffect, useLayoutEffect, useState } from 'react';
import { clearWorkosAuth, hasWorkosSession, isSigningOut, registerWorkosAuth } from '@/services/workos-auth-bridge';

/**
 * Bridges AuthKit's React-based auth state into the singleton used by axios
 * interceptors. Children render only after the bridge has either registered
 * the access-token function (authenticated) or determined no WorkOS session
 * exists (unauthenticated). This guarantees that by the time any child fires
 * an API request, the WorkOS token is available — no timing races.
 */
export function WorkosAuthBridge({ children }: { children: ReactNode }) {
  const { user, getAccessToken, signOut, isLoading } = useAuth();

  // Legacy users (no WorkOS session flag) render immediately. WorkOS users
  // wait until the layout-effect below has either registered the token fn
  // or confirmed there's no user, to avoid firing requests with stale state.
  const [bridgeReady, setBridgeReady] = useState(() => !hasWorkosSession());

  useLayoutEffect(() => {
    if (isLoading) return;
    // While a sign-out is in flight, AuthKit's `user` may briefly remain set
    // even though we've initiated the logout. Re-registering here would fight
    // the sign-out (re-set the localStorage flag, re-bind getAccessToken) and
    // produce a flicker / infinite-reload loop. The sign-out path owns the
    // singleton state until it completes.
    if (isSigningOut()) return;
    if (user) {
      registerWorkosAuth(getAccessToken, signOut, user.email);
    } else {
      clearWorkosAuth();
    }
    setBridgeReady(true);
  }, [user, isLoading, getAccessToken, signOut]);

  useEffect(() => {
    return () => {
      clearWorkosAuth();
    };
  }, []);

  if (!bridgeReady) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
      </div>
    );
  }

  return <>{children}</>;
}
